import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

/**
 * Viewport-locked dashboard chrome: the dark sidebar fills the screen and never
 * leaves a light gap on overscroll. Only the main pane scrolls.
 */
export default function DashboardShell({
  renderSidebar,
  brand,
  trailing,
  desktopHeader,
  menuTestId,
  shellClassName = "bg-slate-50",
  asideClassName = "bg-slate-900",
  asideWidthClass = "w-64",
  mobileBarClassName = "bg-slate-900 text-white border-white/5",
  menuButtonClassName = "text-white hover:bg-white/10",
  sheetClassName = "p-0 w-64 max-w-[85vw] bg-slate-900 border-slate-800",
  mainClassName = "",
  contentClassName = "p-4 sm:p-6 lg:p-8 max-w-full",
  overlay,
  children,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  useEffect(() => {
    document.documentElement.classList.add("dash-lock");
    document.body.classList.add("dash-lock");
    return () => {
      document.documentElement.classList.remove("dash-lock");
      document.body.classList.remove("dash-lock");
    };
  }, []);

  return (
    <div
      data-testid="dashboard-shell"
      className={`h-[100dvh] w-full max-w-full overflow-hidden flex flex-col md:flex-row ${shellClassName}`}
    >
      <aside
        data-testid="dashboard-sidebar"
        className={`hidden md:flex flex-col shrink-0 h-full overflow-hidden ${asideWidthClass} ${asideClassName}`}
      >
        {renderSidebar()}
      </aside>

      <header className={`md:hidden shrink-0 h-14 flex items-center px-3 gap-2 border-b ${mobileBarClassName}`}>
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className={menuButtonClassName} data-testid={menuTestId}>
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" hideClose className={`h-full ${sheetClassName}`}>
            {renderSidebar(closeMobile)}
          </SheetContent>
        </Sheet>
        <div className="flex-1 min-w-0">{brand}</div>
        {trailing}
      </header>

      <main className={`flex-1 min-w-0 min-h-0 overflow-y-auto overflow-x-hidden relative dash-scroll ${mainClassName}`}>
        {desktopHeader}
        <div className={contentClassName}>{children}</div>
        {overlay}
      </main>
    </div>
  );
}
